import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ExpenseReport,
  expenseReportsApi,
  ReportStatus,
  getReportProgress,
  shouldPollReport,
  canCancelReport,
} from 'src/services/expense-reports'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { Button } from 'src/components/button'
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { downloadReport } from 'src/utils/download'
import { GenerateReportModal } from './generate-report-modal'
import { Gate } from 'src/components/gate'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'

interface ReportPreviewSidebarProps {
  report: ExpenseReport | null
  onClose: () => void
  onEdit?: (id: string) => void
  isOpen: boolean
}

export const ReportPreviewSidebar: React.FC<ReportPreviewSidebarProps> = ({
  report: initialReport,
  onClose,
  onEdit: _onEdit,
  isOpen,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Read the live cache value so `enabled` reflects the latest known status
  // (writes from generate/cancel mutations or AsyncJobTracker invalidations
  // land here) rather than the snapshot captured when the panel opened.
  const cachedReport = initialReport
    ? queryClient.getQueryData<ExpenseReport>([...expenseReportQueries.all(), initialReport.id])
    : undefined
  const liveReport = cachedReport ?? initialReport

  // Poll for updates when report is actively processing.
  const { data: polledReport } = useQuery({
    ...expenseReportQueries.retrieveWithPolling(initialReport?.id || ''),
    enabled: isOpen && !!liveReport && shouldPollReport(liveReport),
  })

  // Use polled data if available, otherwise use initial report
  const report = polledReport || initialReport

  const { mutate: generateReport, isPending: isGenerating } = useMutation({
    mutationFn: ({ id, templateId }: { id: string; templateId?: string | null }) =>
      expenseReportsApi.csc.generateReport({ id, templateId }),
    onSuccess: (updatedReport) => {
      // Immediately update the polling query cache with the new status
      queryClient.setQueryData([...expenseReportQueries.all(), updatedReport.id], updatedReport)
      queryClient.invalidateQueries({ queryKey: expenseReportQueries.all() })
    },
  })

  const [showGenerateModal, setShowGenerateModal] = useState(false)

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: expenseReportsApi.remove,
    queryKey: expenseReportQueries.all(),
    onSuccess: onClose,
  })

  const { mutate: cancelReport, isPending: isCancelling } = useMutation({
    mutationFn: (id: string) => expenseReportsApi.csc.cancelReport(id),
    onSuccess: (updatedReport) => {
      // Immediately update the polling query cache with the cancelled status
      queryClient.setQueryData([...expenseReportQueries.all(), updatedReport.id], updatedReport)
      queryClient.invalidateQueries({ queryKey: expenseReportQueries.all() })
    },
  })

  const handleRemove = () => {
    if (report) {
      requestDelete(report.id, report.name)
    }
  }

  const handleGenerateReport = () => {
    setShowGenerateModal(true)
  }

  const handleConfirmGenerate = (templateId: string | null) => {
    if (report) {
      generateReport({ id: report.id, templateId })
    }
  }
  const handleDownloadReport = () => {
    if (report && report.asset) {
      // Logic to download report, e.g., calling an API endpoint
      downloadReport(report.asset)
    }
  }

  if (!report) return null

  return (
    <>
      {/* Overlay */}
      <div
        className={clsx('fixed inset-0 z-modal bg-black bg-opacity-50 transition-opacity', {
          'visible opacity-100': isOpen,
          'invisible opacity-0': !isOpen,
        })}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed right-0 top-0 z-modal h-full w-96 transform bg-surface shadow-lg transition-transform duration-300 ease-in-out',
          {
            'translate-x-0': isOpen,
            'translate-x-full': !isOpen,
          },
        )}
        data-testid="report-preview-sidebar"
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4">
            <h2 className="text-lg font-semibold">{t('reportDetail.reportDetails')}</h2>
            <div className="flex items-center space-x-2">
              <Gate permission="reports.delete">
                <Button
                  variant="danger"
                  iconOnly
                  tooltip={t('reportDetail.deleteReport')}
                  onClick={handleRemove}
                  disabled={dialogProps.isPending}
                  data-testid="remove-report-button"
                >
                  <TrashIcon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </Gate>
              <Button
                onClick={onClose}
                variant="ghost"
                tone="neutral"
                iconOnly
                tooltip={t('common.close')}
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-text-placeholder">{t('common.name')}</p>
                <p className="font-semibold" data-testid="sidebar-report-name">
                  {report.name}
                  {report.organizationRef ? ` - ${report.organizationRef.name}` : ''}
                </p>
              </div>
              {report.notes && (
                <div>
                  <p className="text-sm text-text-placeholder">{t('common.notes')}</p>
                  <p data-testid="sidebar-report-notes">{report.notes}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-text-placeholder">{t('reportDetail.submitted')}</p>
                <p data-testid="sidebar-report-is-submitted">
                  {report.isSubmitted ? t('common.yes') : t('common.no')}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-placeholder">{t('common.approved')}</p>
                <p data-testid="sidebar-report-is-approved">
                  {report.isApproved ? t('common.yes') : t('common.no')}
                </p>
              </div>
              {report.organizationRef && (
                <div>
                  <p className="text-sm text-text-placeholder">{t('reportsPage.organization')}</p>
                  <p data-testid="sidebar-report-organization">{report.organizationRef.name}</p>
                </div>
              )}
              {/* Status Section */}
              <div>
                <p className="text-sm text-text-placeholder">{t('common.status')}</p>
                <span
                  data-testid="sidebar-report-status"
                  className={clsx('inline-block rounded-full px-2 py-1 text-xs font-semibold', {
                    'bg-green-200 text-green-800': report.status === ReportStatus.COMPLETE,
                    'bg-error-200 text-error-800': report.status === ReportStatus.FAILED,
                    'bg-accent-200 text-accent-800': report.status === ReportStatus.QUEUED,
                    'bg-gray-300 text-gray-700': report.status === ReportStatus.CANCELLED,
                    'bg-yellow-200 text-yellow-800':
                      report.status === ReportStatus.PENDING ||
                      report.status === ReportStatus.IN_PROGRESS,
                    'bg-gray-200 text-text-primary':
                      report.status === ReportStatus.NOT_STARTED || !report.status,
                  })}
                >
                  {report.status === ReportStatus.IN_PROGRESS ||
                  report.status === ReportStatus.PENDING
                    ? getReportProgress(report) !== null
                      ? t('reportDetail.statusProcessingPercent', {
                          progress: getReportProgress(report),
                        })
                      : t('reportDetail.statusProcessing')
                    : report.status === ReportStatus.QUEUED
                    ? t('reportDetail.statusQueued')
                    : report.status === ReportStatus.CANCELLED
                    ? t('reportDetail.statusCancelled')
                    : report.status === ReportStatus.NOT_STARTED || !report.status
                    ? t('reportDetail.statusNotStarted')
                    : report.status}
                </span>
              </div>
              {/* Progress info when actively processing */}
              {(report.status === ReportStatus.PENDING ||
                report.status === ReportStatus.QUEUED ||
                report.status === ReportStatus.IN_PROGRESS) &&
                report.startedAt && (
                  <div>
                    <p className="text-sm text-text-placeholder">
                      {report.status === ReportStatus.QUEUED
                        ? t('reportDetail.queuedAt')
                        : t('reportDetail.started')}
                    </p>
                    <p data-testid="sidebar-report-started-at">
                      {new Date(report.startedAt).toLocaleString()}
                    </p>
                  </div>
                )}
              {/* Last generated time */}
              {report.dateReportGenerated && (
                <div>
                  <p className="text-sm text-text-placeholder">{t('reportDetail.lastGenerated')}</p>
                  <p data-testid="sidebar-report-date-generated">
                    {new Date(report.dateReportGenerated).toLocaleString()}
                  </p>
                </div>
              )}
              {/* Error message */}
              {(report.status === ReportStatus.FAILED ||
                report.status === ReportStatus.CANCELLED) &&
                report.errorMessage && (
                  <div>
                    <p className="text-sm text-text-placeholder">
                      {report.status === ReportStatus.CANCELLED
                        ? t('reportDetail.cancellationNote')
                        : t('common.error')}
                    </p>
                    <p
                      className={clsx('text-sm', {
                        'text-error-600': report.status === ReportStatus.FAILED,
                        'text-text-secondary': report.status === ReportStatus.CANCELLED,
                      })}
                      data-testid="sidebar-report-error"
                    >
                      {report.errorMessage}
                    </p>
                  </div>
                )}
              {report.asset && (
                <div>
                  <p className="mb-2 text-sm text-text-placeholder">{t('reportDetail.asset')}</p>
                  <Button
                    variant="ghost"
                    tone="primary"
                    iconOnly
                    onClick={() => window.open(report.asset, '_blank')}
                    tooltip={t('reportDetail.viewAsset')}
                    data-testid="view-report-asset"
                  >
                    <ArrowTopRightOnSquareIcon className="h-6 w-6" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t p-4">
            <Gate permission="reports.edit">
              {canCancelReport(report) ? (
                <Button
                  variant="danger"
                  onClick={() => cancelReport(report.id)}
                  className="mb-2 w-full"
                  disabled={isCancelling}
                  data-testid="cancel-report-button"
                >
                  <XMarkIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                  {isCancelling ? t('reportDetail.cancelling') : t('common.cancel')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleGenerateReport}
                  className="mb-2 w-full"
                  disabled={
                    isGenerating ||
                    report.status === ReportStatus.PENDING ||
                    report.status === ReportStatus.QUEUED ||
                    report.status === ReportStatus.IN_PROGRESS
                  }
                >
                  <SparklesIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                  {isGenerating ||
                  report.status === ReportStatus.PENDING ||
                  report.status === ReportStatus.QUEUED ||
                  report.status === ReportStatus.IN_PROGRESS
                    ? report.status === ReportStatus.QUEUED
                      ? t('reportDetail.queuedEllipsis')
                      : t('reportDetail.generatingReport')
                    : t('common.generate')}
                </Button>
              )}
            </Gate>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                iconOnly
                tooltip={t('reportDetail.downloadReport')}
                onClick={handleDownloadReport}
                disabled={!report?.asset}
                data-testid="download-report-button"
              >
                <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
              <Gate permission="reports.edit">
                <Button
                  variant="secondary"
                  iconOnly
                  tooltip={t('reportDetail.editReport')}
                  onClick={() => {
                    onClose()
                    navigate(`/expense-reports/report/${report.id}`)
                  }}
                  data-testid="edit-report-button"
                >
                  <PencilIcon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </Gate>
            </div>
          </div>
        </div>
      </div>

      <GenerateReportModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleConfirmGenerate}
        organizationId={report.organization}
        defaultTemplateRef={report.defaultTemplateRef}
        isGenerating={isGenerating}
      />

      <ConfirmDeleteDialog {...dialogProps} />
    </>
  )
}
